'use client';
import React from 'react';

import { InView } from 'react-intersection-observer';

import { Collapse, Divider, Checkbox, CheckboxGroup, Button, NoResults } from '@benzinga/core-ui';
import styles from './News.module.scss';
import { SessionContext } from '@benzinga/session-context';

import { useSponsoredContentArticle } from '@benzinga/content-manager-hooks';
import { isGlobalImpressionStored, storeGlobalImpression } from '@benzinga/content-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import type { News as NewsI, NodeQueryParams, StoryObject } from '@benzinga/basic-news-manager';

import { useTranslation } from 'react-i18next';
import { useImpressionTracker } from '@benzinga/analytics';
import { isProOnlyPost } from '@benzinga/article-manager';
import ContentHeadline from '../ContentHeadline';

import { Hr } from '../utils/styletron';
import { splitQuoteNewsByDate } from './utils';

type NewsChannelsCheckboxProps = {
  channelNames: string[];
  checkAll: boolean;
  onChangeCheckAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeOption: (list: string[]) => void;
  selectedChannels: string[];
  t: (key: string) => string;
};

const NewsChannelsCheckbox: React.FC<NewsChannelsCheckboxProps> = ({
  channelNames,
  checkAll,
  onChangeCheckAll,
  onChangeOption,
  selectedChannels,
  t,
}) => (
  <div className="channels-checkbox-container rounded p-2">
    <Checkbox
      checked={checkAll}
      disabled={checkAll}
      label={t('Quote.News.all-news')}
      onChange={onChangeCheckAll}
      style={{ fontWeight: 600 }}
    />
    <Divider thickness="thin" />
    <CheckboxGroup onChange={onChangeOption} options={channelNames} selected={selectedChannels} />
  </div>
);

const News: React.FC<{ initialNews: NewsI[]; symbol: string }> = ({ initialNews, symbol }) => {
  const { t, i18n } = useTranslation('quote');
  const session = React.useContext(SessionContext);
  const newsContainerRef = React.useRef<HTMLDivElement | null>(null);
  useImpressionTracker({ containerRef: newsContainerRef });

  const channels = React.useMemo(
    () => [
      { key: 'General', label: t('Channels.general'), value: 18467 },
      { key: 'Contracts', label: t('Channels.contracts'), value: 87 },
      { key: 'Dividends', label: t('Channels.dividends'), value: 41 },
      // { label: 'Dividends', value: 61 },
      { key: 'Events', label: t('Channels.events'), value: 29618 },
      { key: 'FDA', label: t('Channels.fda'), value: 59 },
      { key: 'M&A', label: t('Channels.m-a'), value: 64 },
      { key: 'Offerings', label: t('Channels.offerings'), value: 65 },
      { key: 'Stock Split', label: t('Channels.stock-split'), value: 66 },
      { key: 'Media', label: t('Channels.media'), value: 5 },
      { key: 'Buybacks', label: t('Channels.buybacks'), value: 63 },
      { key: 'Insider Trades', label: t('Channels.insider-trades'), value: 62 },
      { key: 'Earnings', label: t('Channels.earnings'), value: 16888 },
      { key: 'Guidance', label: t('Channels.guidance'), value: 16889 },
      { key: 'Analyst Ratings', label: t('Channels.analyst-ratings'), value: 67 },
      { key: 'Trading Ideas', label: t('Channels.trading-ideas'), value: 22 },
    ],
    [t],
  );

  const pageSize = 20;
  const [news, setNews] = React.useState<NewsI[]>(initialNews ?? []);
  const [checkAll, setCheckAll] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [page, setPage] = React.useState(() =>
    Math.max(0, Math.ceil((initialNews?.length ?? 0) / pageSize) - 1),
  );
  const [hasMore, setHasMore] = React.useState(true);
  const channelNames: string[] = channels.map(channel => channel.label);
  const [selectedChannels, setSelectedChannels] = React.useState(channelNames);
  const [sponsoredArticles] = useSponsoredContentArticle(session);

  const baseQuery: NodeQueryParams = React.useMemo(
    () => ({ displayOutput: 'full', page: 0, pageSize, symbols: [symbol] }),
    [symbol],
  );

  const buildQuery = React.useCallback(
    (pageNum: number, channelLabels?: string[]): NodeQueryParams => {
      const labels = channelLabels ?? selectedChannels;
      const channelIds = channels
        .filter(channel => labels.includes(channel.label))
        .map(channel => channel.value.toString());
      return channelIds.length === channels.length
        ? { ...baseQuery, page: pageNum }
        : { ...baseQuery, channels: channelIds, page: pageNum };
    },
    [baseQuery, channels, selectedChannels],
  );

  const fetchNews = React.useCallback(
    async (query: NodeQueryParams, append: boolean) => {
      if (append && !hasMore) return;
      setIsLoading(true);
      const sessionObj = session.getSession();
      const { getQuoteNews } = await import('../../utils/quote/tab-data/shared');
      const res = await getQuoteNews(sessionObj, query);
      const newNews = res?.ok?.[0]?.nodes ?? [];
      const pageSizeActual = query.pageSize ?? pageSize;

      if (append) {
        setNews(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const onlyNew = newNews.filter(item => !existingIds.has(item.id));
          return onlyNew.length > 0 ? [...prev, ...onlyNew] : prev;
        });
        setPage(query.page ?? 0);
        if (newNews.length < pageSizeActual) setHasMore(false);
      } else {
        setNews(Array.isArray(newNews) ? newNews : []);
        setPage(0);
        setHasMore(newNews.length >= pageSizeActual);
      }
      setIsLoading(false);
    },
    [hasMore, session],
  );

  const handleOnChangeOption = React.useCallback(
    (list: string[]) => {
      if (checkAll) {
        channels.filter(channel => {
          if (!list.includes(channel.label)) {
            setSelectedChannels([channel.label]);
            fetchNews(buildQuery(0, [channel.label]), false);
            return channel;
          }
          return false;
        });
        setCheckAll(false);
        return;
      } else if (!checkAll && list.length === 0) {
        setSelectedChannels(channelNames);
        setCheckAll(true);
      } else {
        setSelectedChannels(list);
        setCheckAll(list.length === channelNames.length);
      }

      fetchNews(buildQuery(0, list.length === 0 ? channelNames : list), false);
    },
    [buildQuery, channelNames, checkAll, channels, fetchNews],
  );

  const handleOnChangeCheckAll = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setSelectedChannels(checked ? channelNames : []);
      setCheckAll(checked);
      fetchNews(buildQuery(0, checked ? channelNames : []), false);
    },
    [buildQuery, channelNames, fetchNews],
  );

  const handleLoadMore = React.useCallback(() => {
    fetchNews(buildQuery(page + 1), true);
  }, [buildQuery, fetchNews, page]);

  const collapseHeader = checkAll
    ? 'Categories: All'
    : `Categories: ${selectedChannels && selectedChannels.length > 0 && selectedChannels.join(', ')}`;

  const groupedNews = React.useMemo(() => splitQuoteNewsByDate(news, i18n.language), [news, i18n.language]);

  const sponsoredList = React.useMemo(
    () => (Array.isArray(sponsoredArticles) ? sponsoredArticles.filter(Boolean) : []),
    [sponsoredArticles],
  );

  const initialSponsoredPlacement = 3;
  const sponsoredNthPlacement = 4;

  const renderSponsoredArticle = React.useCallback(
    (article: NewsI) => {
      if (Array.isArray(sponsoredArticles)) {
        const registerImpression = isVisible => {
          if (isVisible && !isGlobalImpressionStored(`${article.id}`)) {
            storeGlobalImpression(`${article.id}`);
            session.getManager(TrackingManager).trackCampaignEvent('view', {
              partner_id: (article?.meta?.Reach?.Disclosure?.tid || 0).toString(),
              unit_type: `news-content-article-${article.id}`,
            });
          }
        };

        if (article) {
          return (
            <InView onChange={isVisible => registerImpression(isVisible)} rootMargin="100px">
              <ContentHeadline content={article} key={article.id} />
            </InView>
          );
        }
      }

      return null;
    },
    [session, sponsoredArticles],
  );

  return (
    <div className={styles.newsContainer}>
      <div>
        <div className="collapse-wrapper">
          <Collapse defaultCollapsed={true} header={collapseHeader}>
            <NewsChannelsCheckbox
              channelNames={channelNames}
              checkAll={checkAll}
              onChangeCheckAll={handleOnChangeCheckAll}
              onChangeOption={handleOnChangeOption}
              selectedChannels={selectedChannels}
              t={t}
            />
          </Collapse>
        </div>
        <div className="desktop">
          <NewsChannelsCheckbox
            channelNames={channelNames}
            checkAll={checkAll}
            onChangeCheckAll={handleOnChangeCheckAll}
            onChangeOption={handleOnChangeOption}
            selectedChannels={selectedChannels}
            t={t}
          />
        </div>
        <div className="news-content" ref={newsContainerRef}>
          {groupedNews.map(group => (
            <div key={`news-content-group-item-${group.date}`}>
              <div className="date-heading flex justify-between items-center">
                <h2 className="underline text-xl">{group.date}</h2>
                <Hr className="date-heading-hr flex-grow border-b relative" />
              </div>
              <ul>
                {group.articles.map((article, index) => {
                  const isProOnly = isProOnlyPost(article as StoryObject);
                  return (
                    <React.Fragment key={`news-content-article-${article.id}`}>
                      {sponsoredList.length > 0 &&
                        (index === initialSponsoredPlacement ||
                          (index > initialSponsoredPlacement &&
                            (index - initialSponsoredPlacement) % sponsoredNthPlacement === 0)) && (
                          <li key={`news-content-article-sponsored-${group.date}-${index}`}>
                            {renderSponsoredArticle(
                              (sponsoredList[Math.floor((index - initialSponsoredPlacement) / sponsoredNthPlacement)] ||
                                sponsoredList[0]) as NewsI,
                            )}
                          </li>
                        )}
                      <li>
                        {article?.meta?.Reach ? (
                          renderSponsoredArticle(article)
                        ) : (
                          <ContentHeadline content={article} impression isProOnly={isProOnly} key={article.id} />
                        )}
                      </li>
                    </React.Fragment>
                  );
                })}
              </ul>
            </div>
          ))}
          {news.length === 0 && <NoResults title={`There are no results`} />}
          {initialNews.length >= 50 && (
            <Button
              className="w-full mt-4 uppercase"
              disabled={!hasMore}
              isLoading={isLoading}
              onClick={handleLoadMore}
              variant="flat-light-blue"
            >
              {t('Buttons.show-more')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default News;
